/**
 * Organisation feature definitions.
 * Each feature has a description and a default enabled/disabled state.
 * Add new features here.
 */
export const ORGANISATION_FEATURES = {
  skill_recommendations: {
    description:
      "Enable the weekly skill recommendation engine for this organisation's users",
    default: false,
  },
  disable_magic_link_login: {
    description:
      "When enabled, this organisation's users must sign in through an identity provider. A sign-in link only proves control of a mailbox, so it bypasses the conditional access and MFA the provider enforces.",
    default: false,
  },
  disable_calendar: {
    description: 'No calendar integration or notetaker for this organisation.',
    default: false,
  },
  disable_google_login: {
    description: "When enabled, this organisation's users cannot sign in with Google.",
    default: false,
  },
  disable_microsoft_login: {
    description: "When enabled, this organisation's users cannot sign in with Microsoft.",
    default: false,
  },
  webapp: {
    description:
      "Enables steering this organisation's users to the web app: links, buttons and the web-app onboarding. Off means Wundo never points them at a page — turn it on the day you want to start bringing this organisation into the web app.",
    default: false,
  },
  meetings: {
    description:
      "Wundo works with this organisation's calendars and meetings: the meeting tools, the meeting skills and the chat guidance about them. Off means the chat has no notion of meetings at all — turn it on for an organisation whose users connect a calendar.",
    default: false,
  },
  journeys: {
    description:
      "Journeys for this organisation's managers: a fixed series of 1:1s with one employee (such as a starter's first year), with dated steps, agenda suggestions and reminders. Off means no manager sees a journey, even when staff already set one up.",
    default: false,
  },
  chat_tool_activity: {
    description:
      'While Wundo works on a reply in the app\'s chat, it shows each step as it happens, in plain words ("Ik bekijk de meeting …"), ticked off when done and kept above the reply. Never tool names, ids or results.',
    default: false,
  },
  admin_dashboard: {
    description:
      "This organisation's admins see the Dashboard in the app (bèta): how many of their people started, how Wundo supports their 1:1s and how they practise. Group figures only, never a name.",
    default: false,
  },
  internal_notetaker: {
    description:
      "Every Teams and Google Meet meeting of this organisation's people with the notetaker on is recorded by Wundo's own notetaker instead of the notetaker bot, also meetings other companies organise. It joins as a visible outside participant named Wundo: in Teams signed in with Wundo's own Microsoft account, in Google Meet as a guest without a Google account. It asks to join once and waits up to 20 minutes to be let in. When it never got to ask, the notetaker bot takes over; once it asked, no bot is sent. Off, or while the notetaker is not set up in this environment, these meetings keep the notetaker bot.",
    default: false,
  },
  eu_only: {
    description:
      "This organisation's data may only be processed by services that run in the EU. A spoken practice on the pipeline speaks with an Azure voice in West Europe instead of ElevenLabs; the other services that leave the EU will check it one by one.",
    default: false,
  },
  demo: {
    description:
      'A demo organisation: in the app its users can only practise the existing library and edit their own profile, such as their language. Every other nav tab, every other settings tab and the buttons to write or edit a practice conversation are greyed out and open a "not available in this demo" dialog instead. The chat drawer is gone, and they skip the onboarding and land in the practice library.',
    default: false,
  },
} as const satisfies Record<string, { description: string; default: boolean }>;

export type OrganisationFeature = keyof typeof ORGANISATION_FEATURES;

export type OrganisationFeatures = Partial<Record<OrganisationFeature, boolean>>;

export const ORGANISATION_FEATURE_NAMES = Object.keys(ORGANISATION_FEATURES) as [
  OrganisationFeature,
  ...OrganisationFeature[],
];

export type ResolvedOrganisationFeatures = Record<OrganisationFeature, boolean>;

export function resolveOrganisationFeatures(
  stored: OrganisationFeatures | null | undefined,
): ResolvedOrganisationFeatures {
  return Object.fromEntries(
    ORGANISATION_FEATURE_NAMES.map((name) => [
      name,
      stored?.[name] ?? ORGANISATION_FEATURES[name].default,
    ]),
  ) as ResolvedOrganisationFeatures;
}
